import React, { useState, useEffect } from "react";
import { X, FileText, Package, Plus } from "lucide-react";
import axios from "axios";
import { BASE_URL } from "../../Components/API/Api";
import Swal from "sweetalert2";
import classes from "./FinishedGoodsModal.module.css";

const FinishedGoodsModal = ({ isOpen, onClose, refreshList }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [generatingNumber, setGeneratingNumber] = useState(false);
  const [productionOrders, setProductionOrders] = useState([]);
  const [formData, setFormData] = useState({
    transfer_number: "",
    production_order_id: "",
    transfer_date: new Date().toISOString().split("T")[0],
    materials: [
      {
        product_id: "",
        product_name: "",
        uom: "",
        qty_planned: "",
        qty_produced: "",
        cost_of_production: "",
        unit_cost: "",
        profit_margin: "",
        total_value: ""
      }
    ],
    total_transfer_value: 0
  });

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchIssuedProductionOrders();
      generateTransferNumber();
    }
  }, [isOpen]);

  // Generate transfer number from API
  const generateTransferNumber = async () => {
    try {
      setGeneratingNumber(true);
      const token = localStorage.getItem("authToken");

      const response = await axios.get(
        `${BASE_URL}/v1/finished_goods_transfer/generate_transfer_number`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.status && response.data.data) {
        setFormData(prev => ({
          ...prev,
          transfer_number: response.data.data
        }));
      } else {
        // Fallback to local generation if API fails
        generateLocalTransferNumber();
      }
    } catch (error) {
      console.error("Error generating transfer number:", error);
      // Fallback to local generation
      generateLocalTransferNumber();
    } finally {
      setGeneratingNumber(false);
    }
  };

  // Local fallback for transfer number generation
  const generateLocalTransferNumber = () => {
    const timestamp = new Date().getTime();
    const randomNum = Math.floor(Math.random() * 1000);
    const transferNum = `TRF-${timestamp.toString().slice(-6)}-${randomNum}`;
    setFormData(prev => ({ ...prev, transfer_number: transferNum }));
  };

  // Fetch issued production orders
  const fetchIssuedProductionOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      const response = await axios.get(
        `${BASE_URL}/v1/production_order/issued`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.status && Array.isArray(response.data.data)) {
        setProductionOrders(response.data.data);
      } else {
        setProductionOrders([]);
        Swal.fire("Info", "No issued production orders found", "info");
      }
    } catch (error) {
      console.error("Error fetching issued production orders:", error);
      Swal.fire("Error", "Failed to load production orders", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Calculate unit cost based on cost of production and quantity produced
  const calculateUnitCost = (costOfProduction, quantityProduced) => {
    if (!costOfProduction || !quantityProduced || parseFloat(quantityProduced) === 0) {
      return "";
    }
    const cost = parseFloat(costOfProduction);
    const qty = parseFloat(quantityProduced);
    return (cost / qty).toFixed(2);
  };

  // Calculate selling price (not displayed but used for total value)
  const calculateSellingPrice = (unitCost, profitMargin) => {
    if (!unitCost || unitCost === "") return 0;
    const cost = parseFloat(unitCost);
    const margin = parseFloat(profitMargin) || 0;
    return cost * (1 + (margin / 100));
  };

  // Calculate total value
  const calculateTotalValue = (sellingPrice, quantityProduced) => {
    if (!sellingPrice || !quantityProduced) return "";
    const price = parseFloat(sellingPrice);
    const qty = parseFloat(quantityProduced);
    return (price * qty).toFixed(2);
  };

  // Handle material row changes
  const handleMaterialChange = (index, field, value) => {
    const updatedMaterials = [...formData.materials];
    updatedMaterials[index][field] = value;

    // Recalculate unit cost if cost_of_production or qty_produced changes
    if (field === 'cost_of_production' || field === 'qty_produced') {
      const costOfProduction = updatedMaterials[index].cost_of_production;
      const qtyProduced = updatedMaterials[index].qty_produced;

      // Calculate new unit cost
      const unitCost = calculateUnitCost(costOfProduction, qtyProduced);
      updatedMaterials[index].unit_cost = unitCost;
    }

    // Calculate total value if any relevant fields change
    if (field === 'cost_of_production' || field === 'qty_produced' || field === 'profit_margin') {
      const unitCost = updatedMaterials[index].unit_cost || "";
      const profitMargin = updatedMaterials[index].profit_margin || "";
      const qtyProduced = updatedMaterials[index].qty_produced || "";

      // Only calculate if all required fields have values
      if (unitCost && profitMargin && qtyProduced) {
        // Calculate selling price and total value
        const sellingPrice = calculateSellingPrice(unitCost, profitMargin);
        const totalValue = calculateTotalValue(sellingPrice, qtyProduced);
        updatedMaterials[index].total_value = totalValue;
      } else {
        updatedMaterials[index].total_value = "";
      }

      // Update total transfer value
      const totalTransferValue = updatedMaterials.reduce((sum, material) => {
        return sum + (parseFloat(material.total_value) || 0);
      }, 0);

      setFormData(prev => ({
        ...prev,
        materials: updatedMaterials,
        total_transfer_value: totalTransferValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        materials: updatedMaterials
      }));
    }
  };

  // Add new material row
  const addMaterialRow = () => {
    setFormData(prev => ({
      ...prev,
      materials: [
        ...prev.materials,
        {
          product_id: "",
          product_name: "",
          uom: "",
          qty_planned: "",
          qty_produced: "",
          cost_of_production: "",
          unit_cost: "",
          profit_margin: "",
          total_value: ""
        }
      ]
    }));
  };

  // Remove material row
  const removeMaterialRow = (index) => {
    if (formData.materials.length > 1) {
      const updatedMaterials = formData.materials.filter((_, i) => i !== index);
      const totalTransferValue = updatedMaterials.reduce((sum, material) => {
        return sum + (parseFloat(material.total_value) || 0);
      }, 0);

      setFormData(prev => ({
        ...prev,
        materials: updatedMaterials,
        total_transfer_value: totalTransferValue
      }));
    }
  };

  // Handle production order selection
  const handleProductionOrderSelect = async (orderId) => {
    if (!orderId) {
      // Reset form if no order selected
      setFormData(prev => ({
        ...prev,
        production_order_id: "",
        materials: [
          {
            product_id: "",
            product_name: "",
            uom: "",
            qty_planned: "",
            qty_produced: "",
            cost_of_production: "",
            unit_cost: "",
            profit_margin: "",
            total_value: ""
          }
        ],
        total_transfer_value: 0
      }));
      return;
    }

    try {
      // Find the selected order from the already fetched list
      const selectedOrder = productionOrders.find(order => order.id.toString() === orderId);

      if (selectedOrder && selectedOrder.product) {
        const product = selectedOrder.product;

        // Calculate total material cost from raw_material_requisitions
        const totalMaterialCost = selectedOrder.raw_material_requisitions?.reduce((sum, material) => {
          return sum + (parseFloat(material.total_cost) || 0);
        }, 0) || 0;

        // Get planned and produced quantities
        const plannedQty = selectedOrder.planned_quantity || "";
        const producedQty = selectedOrder.quantity_completed || plannedQty || "";

        const material = {
          product_id: product.id,
          product_name: product.name,
          uom: product.unit || "Each",
          qty_planned: plannedQty,
          qty_produced: producedQty,
          cost_of_production: "", // Leave empty for user to fill
          unit_cost: "", // Will be calculated when user enters cost of production
          profit_margin: "", // Leave empty for user to fill
          total_value: "" // Will be calculated when all fields are filled
        };

        setFormData(prev => ({
          ...prev,
          production_order_id: orderId,
          materials: [material],
          total_transfer_value: 0
        }));
      }
    } catch (error) {
      console.error("Error processing production order:", error);
      Swal.fire("Error", "Failed to load production order details", "error");
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Disable button immediately and prevent multiple submissions
    setSubmitting(true);

    // Validation
    if (!formData.transfer_number || !formData.production_order_id || !formData.transfer_date) {
      Swal.fire("Error", "Please fill in all required fields", "error");
      setSubmitting(false);
      return;
    }

    if (formData.materials.some(m => !m.product_id || !m.qty_produced || !m.cost_of_production || !m.profit_margin)) {
      Swal.fire("Error", "Please fill in all material details including cost of production and profit margin", "error");
      setSubmitting(false);
      return;
    }

    try {
      const token = localStorage.getItem("authToken");

      // Prepare payload according to API specification
      const payload = {
        transfer_no: formData.transfer_number,
        production_order_id: formData.production_order_id,
        transfer_date: formData.transfer_date,
        target_product_id: formData.materials[0].product_id,
        uom: formData.materials[0].uom,
        produced_quantity: formData.materials[0].qty_produced,
        cost_of_production: formData.materials[0].cost_of_production,
        unit_cost: formData.materials[0].unit_cost,
        profit_margin: formData.materials[0].profit_margin,
        total_value: formData.materials[0].total_value
      };

      console.log("Sending payload:", payload);

      // Make API call to create finished goods transfer
      const response = await axios.post(
        `${BASE_URL}/v1/finished_goods_transfer/create`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("Backend response:", response.data);

      if (response.data?.status) {
        Swal.fire({
          title: "Success",
          text: response.data.message || "Transfer completed successfully!",
          icon: "success",
          timer: 1500,
          showConfirmButton: false
        }).then(() => {
          // Reset form
          setFormData({
            transfer_number: "",
            production_order_id: "",
            transfer_date: new Date().toISOString().split("T")[0],
            materials: [
              {
                product_id: "",
                product_name: "",
                uom: "",
                qty_planned: "",
                qty_produced: "",
                cost_of_production: "",
                unit_cost: "",
                profit_margin: "",
                total_value: ""
              }
            ],
            total_transfer_value: 0
          });

          // Close modal
          onClose();

          // Refresh parent list if needed
          if (refreshList) {
            refreshList();
          }
        });
      } else {
        // Show the exact backend error message
        Swal.fire("Error", response.data?.message || "Failed to complete transfer", "error");
      }
    } catch (error) {
      console.error("Error completing transfer:", error);

      // Show the exact backend error message if available
      if (error.response?.data?.message) {
        Swal.fire("Error", error.response.data.message, "error");
      } else if (error.response?.data?.error) {
        Swal.fire("Error", error.response.data.error, "error");
      } else if (error.message) {
        Swal.fire("Error", error.message, "error");
      } else {
        Swal.fire("Error", "Something went wrong. Please try again.", "error");
      }
    } finally {
      // Re-enable the button after the request is complete
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={classes.overlay}>
      <div className={classes.modal}>
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.title}>
            <FileText className={classes.icon} />
            <h2>Transfer To Finished Goods</h2>
          </div>
          <X className={classes.closeIcon} onClick={onClose} />
        </div>

        <p className={classes.subtitle}>
          Record transfer of completed goods from production floor to finished goods warehouse.
        </p>

        {/* Form */}
        <form className={classes.form} onSubmit={handleSubmit}>
          <div className={classes.row}>
            <div className={classes.field}>
              <label>Transfer No</label>
              <div className={classes.transferNumberContainer}>
                <input
                  type="text"
                  name="transfer_number"
                  placeholder="Transfer number"
                  value={generatingNumber ? "Generating..." : formData.transfer_number}
                  readOnly
                  className={classes.readOnlyInput}
                />
                {generatingNumber && (
                  <div className={classes.loadingIndicator}>...</div>
                )}
              </div>
            </div>

            <div className={classes.field}>
              <label>Production Order No</label>
              <select
                name="production_order_id"
                value={formData.production_order_id}
                onChange={(e) => {
                  handleInputChange(e);
                  handleProductionOrderSelect(e.target.value);
                }}
                disabled={loading}
              >
                <option value="">
                  {loading ? "Loading production orders..." : "-- Select Production Order --"}
                </option>
                {!loading && productionOrders.map((order) => (
                  <option key={order.id} value={order.id}>
                    {order.production_number}
                  </option>
                ))}
              </select>
            </div>

            <div className={classes.field}>
              <label>Transfer Date *</label>
              <input
                type="date"
                name="transfer_date"
                value={formData.transfer_date}
                onChange={handleInputChange}
                max={new Date().toISOString().split("T")[0]}
              />
            </div>
          </div>

          {/* Finished Goods Details Section */}
          <div className={classes.sectionHeader}>
            <Package className={classes.sectionIcon} />
            <h3>Finished Goods Details</h3>
          </div>

          <div className={classes.tableWrapper}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>UOM</th>
                  <th>Qty Planned</th>
                  <th>Qty Produced</th>
                  <th>Cost of Production</th>
                  <th>Unit Cost</th>
                  <th>Profit Margin (%)</th>
                  <th>Total Value</th>
                </tr>
              </thead>
              <tbody>
                {formData.materials.map((row, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="text"
                        placeholder="Product"
                        value={row.product_name}
                        readOnly
                        className={classes.readOnlyInput}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        placeholder="UOM"
                        value={row.uom}
                        readOnly
                        className={classes.readOnlyInput}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        placeholder="Planned"
                        value={row.qty_planned}
                        readOnly
                        className={classes.readOnlyInput}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        placeholder="Produced"
                        value={row.qty_produced}
                        onChange={(e) => handleMaterialChange(index, 'qty_produced', e.target.value)}
                        min="1"
                        required
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        placeholder="Cost of Production"
                        value={row.cost_of_production}
                        onChange={(e) => handleMaterialChange(index, 'cost_of_production', e.target.value)}
                        step="0.01"
                        min="0"
                        required
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        placeholder="Unit Cost"
                        value={row.unit_cost}
                        readOnly
                        className={classes.readOnlyInput}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        placeholder="Margin %"
                        value={row.profit_margin}
                        onChange={(e) => handleMaterialChange(index, 'profit_margin', e.target.value)}
                        step="0.01"
                        min="0"
                        max="100"
                        required
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        placeholder="Total"
                        value={row.total_value}
                        readOnly
                        className={classes.readOnlyInput}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


          <div className={classes.submitSection}>
            <button
              type="submit"
              className={classes.submitBtn}
              disabled={submitting || !formData.production_order_id || !formData.transfer_number}
            >
              {submitting ? "Processing..." : "Complete Transfer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FinishedGoodsModal;